import clsx from "clsx";
import { useRef, useState } from "react";

import { useTimeRemaining } from "../../utils/hooks/use-time-remaining";

import { TimerControls } from "components/footer/tools-menu/TimerControls";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, MenuInstance, MenuItem } from "mds/components/Menu";
import { RoundedButton } from "mds/components/buttons/RoundedButton";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { ChevronUpIcon, ClockIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectClassTimerEndsAt } from "store/selectors";

export const TimerBanner = ({ isMobile }: { isMobile?: boolean }) => {
  const { canAuthorSection } = useCapabilities();
  const endTime = useAppSelector(selectClassTimerEndsAt);
  const endDate = endTime ? new Date(endTime) : null;
  const { timeRemainingStrPadded } = useTimeRemaining(endDate);
  const menuRef = useRef<MenuInstance>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  if (!endTime || !timeRemainingStrPadded) return null;

  const MenuButton = isMobile ? (
    <SquareIconButton
      aria-label={t("footer.tool.timer_title")}
      eventName="Mobile Classroom Timer menu opened"
      kind="secondary"
      size="xl"
      isSelected
    >
      <ClockIcon />
    </SquareIconButton>
  ) : (
    <RoundedButton
      className={clsx(!canAuthorSection && "cursor-default")}
      eventName="Classroom Timer menu opened"
      kind="secondary"
      title={t("footer.tool.timer_title")}
      isSelected
    >
      <IconText
        iconEnd={canAuthorSection ? <ChevronUpIcon /> : undefined}
        iconStart={<ClockIcon />}
        text={<span className="min-w-[42px] text-left">{timeRemainingStrPadded}</span>}
      />
    </RoundedButton>
  );

  if (!canAuthorSection) {
    if (isMobile) {
      return (
        <Menu
          className="dark-menu"
          direction="top"
          menuButton={MenuButton}
          menuClassName="xs:min-w-[320px]"
        >
          <MenuItem disabled>{t("footer.tool.timer_in_progress")}</MenuItem>
          <MenuDivider />
          <MenuItem disabled>
            <IconText iconStart={<ClockIcon />} text={timeRemainingStrPadded} />
          </MenuItem>
        </Menu>
      );
    }

    return MenuButton;
  }

  return (
    <Menu
      className="dark-menu min-w-[180px]"
      direction="top"
      instanceRef={menuRef}
      menuButton={MenuButton}
      onMenuChange={(e) => setMenuOpen(e.open)}
    >
      {menuOpen ? <TimerControls onClose={() => menuRef.current?.closeMenu()} /> : null}
    </Menu>
  );
};
